import { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import routeConfig from 'routes/config';
import SupportForm from 'services/support';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import treezDashboardLogo from '../../assets/img/brand/treez-nav.svg';
import useAuth from '../../state/auth/hooks/useAuth';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';

const DashboardNavbar = (): JSX.Element => {
  const { organisation } = useOrganisation();

  const { userInvites, activeOrg } = organisation;
  const { auth, toLogout } = useAuth();
  const { isSandbox, user } = auth;
  const isOrgLevel = useOrgLevel();
  let userName = '';

  if (user && user?.user_info && user?.user_info.name) {
    userName = user.user_info.name;
  } else {
    userName = 'Welcome!';
  }
  const logoutWrapper = () => {
    toLogout();
  };

  const myRef = useRef(null);

  useEffect(() => {
    if (myRef.current) {
      const script: HTMLScriptElement = document.createElement('script');
      script.src = 'https://app.getbeamer.com/js/beamer-embed.js';
      script.type = 'text/javascript';
      script.defer = true;
      script.async = true;

      document.body.appendChild(script);
    }
  }, []);

  return (
    <Fragment>
      <nav
        className={cn(
          'shadow-sm h-16 py-4 px-4 pl-4 sticky top-0 z-50 bg-white flex justify-between items-center',
          isSandbox && 'border-b-4 border-sandbox',
          'border-b'
        )}
        id="navbar-main"
      >
        <div className="inline-flex items-center h-5">
          <Link
            to={`/${routeConfig.STORES.layout}/${routeConfig.STORES.LIST.path}`}
          >
            <img className="h-5" src={treezDashboardLogo} alt="TreezPay logo" />
          </Link>
        </div>

        <div className="flex">
          <div className="flex items-center mr-4">
            <span className="text-gray-blue-80 text-sm leading-6">
              {isOrgLevel ? 'Org view' : activeOrg?.name || 'Select Store'}
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={'treez-green'}
                className="relative w-8 h-8 p-0"
                data-testid="profile-icon"
                rounded={'full'}
                iconStart="user"
                iconSize={18}
                iconClassName="text-gray-blue-80"
              >
                {userInvites.length > 0 && (
                  <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 p-1 bg-red-500 rounded-full" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56 z-200">
              <div className="px-2 pt-2">
                <DropdownMenuLabel className="p-0 leading-6 font-normal text-base text-gray-blue-90">
                  {userName}
                </DropdownMenuLabel>
                <p className="py-1.5 text-xs text-gray-blue-70">
                  {user?.user_info?.email}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={SupportForm.openForm}
              >
                <Icon
                  name="message-square"
                  size={16}
                  className="text-gray-blue-50"
                />
                <span>Contact Support</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={logoutWrapper}
              >
                <Icon name="log-out" size={16} className="text-gray-blue-50" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
      {isSandbox && (
        <div className="flex flex-col justify-center items-center sticky top-16 z-99 border-none">
          <span className="bg-sandbox w-47 h-6 flex justify-center items-center rounded-b text-xs font-semibold text-sandbox-text">
            You are in test mode
          </span>
        </div>
      )}
    </Fragment>
  );
};

export default DashboardNavbar;
